import { createTheme } from '@paintscout/ui/styles'
import type { BrandPalette, SecondaryPalette, SemanticPalette } from '@paintscout/ui/styles/palette'
import type { Palette } from '@paintscout/ui/styles'
import type { PresentationStyle } from 'paintscout'
import Color from 'color'
import { getPresentationAccentColor } from './getPresentationButtonColors'
import { defaultPresentationStyle } from './presentationDefaults'
import { CSS_GENERIC_FAMILIES, quoteFontFamily } from './googleFonts/cssFontStack'

/** Fallback `palette.colors.background` used both for the theme's `accentBackground` and by any
 * UI (e.g. Banner block options) that needs to display the same resolved color as a swatch. */
export const ACCENT_BACKGROUND_COLOR_DEFAULT = '#f0f0f0'

// Theme augmentations for PresentationNext
// Targets '@material-ui/core/styles' (not sub-paths) due to re-export chain resolution in TS

declare module '@material-ui/core/styles' {
  interface Theme {
    boxShadow?: {
      1?: string
    }
    palette: Palette
    size: {
      '0': '0px'
      px: '1px'
      '0.5': '0.125rem'
      '1': '0.25rem'
      '1.5': '0.375rem'
      '2': '0.5rem'
      '2.5': '0.625rem'
      '3': '0.75rem'
      '3.5': '0.875rem'
      '4': '1rem'
      '5': '1.25rem'
      '6': '1.5rem'
      '7': '1.75rem'
      '8': '2rem'
      '9': '2.25rem'
      '10': '2.5rem'
      '11': '2.75rem'
      '12': '3rem'
      '14': '3.5rem'
      '16': '4rem'
      '20': '5rem'
      '24': '6rem'
      '28': '7rem'
      '32': '8rem'
      '36': '9rem'
      '40': '10rem'
      '44': '11rem'
      '48': '12rem'
      '52': '13rem'
      '56': '14rem'
      '60': '15rem'
      '64': '16rem'
      '72': '18rem'
      '80': '20rem'
      '96': '24rem'
      '112': '32rem'
      '128': '48rem'
      '144': '64rem'
      auto: 'auto'
      '1/2': '50%'
      '1/3': '33.333333%'
      '2/3': '66.666667%'
      '1/4': '25%'
      '2/4': '50%'
      '3/4': '75%'
      '1/5': '20%'
      '2/5': '40%'
      '3/5': '60%'
      '4/5': '80%'
      '1/6': '16.666667%'
      '2/6': '33.333333%'
      '3/6': '50%'
      '4/6': '66.666667%'
      '5/6': '83.333333%'
      '1/12': '8.333333%'
      '2/12': '16.666667%'
      '3/12': '25%'
      '4/12': '33.333333%'
      '5/12': '41.666667%'
      '6/12': '50%'
      '7/12': '58.333333%'
      '8/12': '66.666667%'
      '9/12': '75%'
      '10/12': '83.333333%'
      '11/12': '91.666667%'
      full: '100%'
      min: 'min-content'
      max: 'max-content'
    }
    borderRadius: {
      none: '0'
      sm: '0.125rem'
      md: '0.25rem'
      lg: '0.5rem'
      xl: '0.75rem'
      '2xl': '1rem'
      '3xl': '1.5rem'
      full: '9999px'
    }
    paintscout: {
      header: { height: { sm: string; md: string; lg?: string } }
      fontScale: number
      palette: {
        primary: string
        dark: string
        light: string
        lighter: string
        secondary: SecondaryPalette
        brand: BrandPalette
        semantic: SemanticPalette
      }
    }
  }

  interface ThemeOptions {
    boxShadow?: { 1?: string }
    size?: Theme['size']
    borderRadius?: Theme['borderRadius']
    paintscout?: {
      header?: { height?: { sm?: string; md?: string; lg?: string } }
      fontScale: number
      palette?: { primary?: string; brand?: BrandPalette }
    }
  }
}

// Matches FALLBACK_FONT_FAMILY in loadPresentationFonts, in the lowercase form stored styles use.
const FALLBACK_FONT = 'inter'

/**
 * Appends `inter` to a font stack, just ahead of its generic family. Every presentation font is
 * fetched from Google at runtime; when one fails the browser walks the rest of the stack, and
 * landing on Inter looks far closer to the intended design than the browser's default face.
 *
 * Also quotes the family names CSS requires quoting — see quoteFontFamily. Stored stacks keep
 * their bare form (`'source sans 3, sans-serif'`); it's only on the way into a stylesheet that the
 * quotes have to be there.
 */
export function withFontFallback(font: string): string {
  if (typeof font !== 'string') return font
  const parts = font
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
  if (!parts.length) return font
  if (!parts.some((part) => part.replaceAll(/^['"]|['"]$/g, '').toLowerCase() === FALLBACK_FONT)) {
    if (CSS_GENERIC_FAMILIES.has(parts.at(-1).toLowerCase())) {
      parts.splice(parts.length - 1, 0, FALLBACK_FONT)
    } else {
      parts.push(FALLBACK_FONT)
    }
  }
  return parts.map((part) => quoteFontFamily(part)).join(', ')
}

export function getPresentationTheme(presentationStyle?: PresentationStyle) {
  const {
    textColor: textColorLegacy,
    fontFamily = 'roboto, sans-serif',
    altFontFamily,
    heading1 = {},
    heading2 = {},
    heading3 = {},
    body = {},
    palette
  } = presentationStyle || defaultPresentationStyle

  // A slot's fontFamily is either an explicit stack or one of two sentinels pointing at the
  // style's two global faces. 'alt' falls back to the primary face when no alt font is set, so a
  // style saved before altFontFamily existed still renders in a real face rather than an
  // unresolvable 'alt' literal.
  const resolveFontFamily = (font: string) => {
    if (font === 'default') return withFontFallback(fontFamily)
    if (font === 'alt') return withFontFallback(altFontFamily || fontFamily)
    return withFontFallback(font)
  }

  const accentColor = getPresentationAccentColor(presentationStyle)
  const textColor = palette?.colors?.text || textColorLegacy
  const accentTextColor = palette?.colors?.accentText || '#444'
  const accentBackgroundColor = palette?.colors?.background || ACCENT_BACKGROUND_COLOR_DEFAULT

  const getTypogColor = (typog: string) => {
    switch (palette?.typogMapping?.[typog]) {
      case 'accentText':
        return accentTextColor
      case 'accent':
        return accentColor
      case 'background':
        return accentBackgroundColor
      default:
        // 'custom' also falls through here — an explicit `heading1.color` etc. always wins over
        // this fallback (see the `heading1.color || getTypogColor('h1')` calls below), so this
        // value is never actually used for 'custom' in practice
        return textColor
    }
  }

  return createTheme({
    palette: {
      primary: { main: accentColor },
      secondary: { main: accentColor },
      text: { primary: textColor, secondary: accentTextColor },
      background: { default: '#fff' },
      accentBackground: { default: accentBackgroundColor }
    },
    typography: {
      fontFamily: withFontFallback(fontFamily),
      h1: {
        color: heading1.color || getTypogColor('h1'),
        ...(heading1.fontFamily && { fontFamily: resolveFontFamily(heading1.fontFamily) }),
        ...(heading1.fontSize && { fontSize: heading1.fontSize }),
        ...(heading1.fontStyle && { fontStyle: heading1.fontStyle }),
        ...(heading1.fontWeight && { fontWeight: heading1.fontWeight }),
        ...(heading1.letterSpacing && { letterSpacing: heading1.letterSpacing }),
        ...(heading1.lineHeight && { lineHeight: heading1.lineHeight }),
        ...(heading1.textTransform && { textTransform: heading1.textTransform })
      },
      h2: {
        color: heading2.color || getTypogColor('h2'),
        ...(heading2.fontFamily && { fontFamily: resolveFontFamily(heading2.fontFamily) }),
        ...(heading2.fontSize && { fontSize: heading2.fontSize }),
        ...(heading2.fontStyle && { fontStyle: heading2.fontStyle }),
        ...(heading2.fontWeight && { fontWeight: heading2.fontWeight }),
        ...(heading2.letterSpacing && { letterSpacing: heading2.letterSpacing }),
        ...(heading2.lineHeight && { lineHeight: heading2.lineHeight }),
        ...(heading2.textTransform && { textTransform: heading2.textTransform })
      },
      h3: {
        color: heading3.color || getTypogColor('h3'),
        ...(heading3.fontFamily && { fontFamily: resolveFontFamily(heading3.fontFamily) }),
        ...(heading3.fontSize && { fontSize: heading3.fontSize }),
        ...(heading3.fontStyle && { fontStyle: heading3.fontStyle }),
        ...(heading3.fontWeight && { fontWeight: heading3.fontWeight }),
        ...(heading3.letterSpacing && { letterSpacing: heading3.letterSpacing }),
        ...(heading3.lineHeight && { lineHeight: heading3.lineHeight }),
        ...(heading3.textTransform && { textTransform: heading3.textTransform })
      },
      h4: { color: textColor },
      h5: { color: textColor },
      h6: { color: accentTextColor },
      subtitle1: { color: textColor },
      subtitle2: { color: accentTextColor },
      body1: {
        color: body.color || getTypogColor('body'),
        ...(body.fontFamily && { fontFamily: resolveFontFamily(body.fontFamily) }),
        ...(body.fontSize && { fontSize: body.fontSize }),
        ...(body.letterSpacing && { letterSpacing: body.letterSpacing }),
        ...(body.lineHeight && { lineHeight: body.lineHeight }),
        ...(body.textTransform && { textTransform: body.textTransform })
      },
      body2: {
        color: body.color ?? textColor,
        ...(body.fontFamily && { fontFamily: resolveFontFamily(body.fontFamily) }),
        ...(body.fontSize && { fontSize: body.fontSize }),
        ...(body.letterSpacing && { letterSpacing: body.letterSpacing }),
        ...(body.lineHeight && { lineHeight: body.lineHeight }),
        ...(body.textTransform && { textTransform: body.textTransform })
      },
      button: { color: Color(accentColor).isLight() ? '#000' : '#FFF' },
      caption: { color: textColor },
      overline: { color: textColor }
    },
    boxShadow: {
      1: '3px 0px 12px -2px rgba(0,0,0,0.23)'
    }
  } as any)
}
